/**
 * New Year avatars — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearAvatarsPack: ToilePack = {
    slug: 'new-year-avatars',
    category: 'season',
    name: 'New Year avatars',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'hat', label: 'Party hat avatar', subject: 'A simple smiling face in a cone party hat inside a round frame, built to read small, isolated'},
        {key: 'clock', label: 'Clock avatar', subject: 'A clock face at midnight inside a round frame with stars, built to read small, isolated'},
        {key: 'glass', label: 'Glass avatar', subject: 'A champagne flute with bubbles inside a round frame, built to read small, isolated'},
    ],
    styles: [
        {key: 'deco', label: 'Art deco', style: 'art deco illustration, geometric fans and sunbursts, clean lines, no outlines', palette: 'black and gold with a little cream', framing: 'Centered on a solid black background with an even margin'},
        {key: 'neon', label: 'Neon party', style: 'neon party graphic, glowing lines, confetti and streamers', palette: 'electric pink, cyan and gold on deep navy', framing: 'Centered on a dark background, built to read clean at small size'},
        {key: 'minimal', label: 'Minimal', style: 'modern minimal illustration, few flat shapes, generous space', palette: 'gold and white with a single black', framing: 'Centered on a plain white background with generous space around it'},
    ],
    directions: ['a fireworks avatar instead', 'wearing sunglasses', 'as a matching pair'],
    directionHint: 'try: a fireworks avatar instead',
    seo: {
        title: 'New Year Avatar: Party Hat, Clock, Champagne',
        metaDescription: 'Nine new year profile picture avatars — party hat face, clock, champagne glass — round and built to read small, three styles.',
        h1: 'New year avatar: party hat, clock, champagne glass',
        tagline: 'Nine avatars, three midnight subjects, three styles each.',
        intro: 'Round profile pictures for the January feed: a party hat face, a clock, a champagne glass, each built simple enough to stay clear at avatar size. Ask for a confetti burst, a countdown numeral, or your own subject, and the same three styles carry it.',
        useCases: [
            'a social media profile picture',
            'a group chat or Discord avatar',
            'a forum or gaming profile icon',
            'a temporary January branding refresh',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine round avatars — a party hat face, a clock, a champagne glass — built to read small, in three styles.'},
            {q: 'Will these look good as a small profile picture?', a: 'Yes — each is built as a simple, bold round design so it stays clear even shrunk to avatar size.'},
            {q: 'Are these based on my own photo?', a: 'No — these are standalone illustrated avatars, not a likeness generated from a photo of you.'},
            {q: 'Can I get a different subject, like confetti?', a: 'Yes — the direction field takes any new year subject, including confetti, a countdown numeral, or a party mask.'},
            {q: 'Can I use these for a business or brand account?', a: 'Yes — check the license terms on your account for the specifics of commercial use.'},
        ],
    },
}
